/**
 * SessionsByModeChart - Pie chart showing session distribution by mode.
 * Displays Interactive, Background, Chatbot, and WorkItemAnalysis modes.
 */
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts'
import { chartDefaults, tooltipStyles } from './chartConfig'
import { PieChart as PieChartIcon } from 'lucide-react'
import type { SessionsByModeDataPoint } from '@/types/adoption'

/**
 * Color mapping for session modes
 */
const modeColors: Record<string, string> = {
  Interactive: 'hsl(217, 91%, 60%)',     // Blue
  Background: 'hsl(142, 76%, 36%)',      // Green
  Chatbot: 'hsl(280, 65%, 60%)',         // Purple
  WorkItemAnalysis: 'hsl(38, 92%, 50%)', // Orange
}

export interface SessionsByModeChartProps {
  /** Array of data points */
  data: SessionsByModeDataPoint[]
  /** Chart height in pixels */
  height?: number
}

/**
 * Custom label renderer for pie chart
 */
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
  cx: number
  cy: number
  midAngle: number
  innerRadius: number
  outerRadius: number
  percent: number
}) => {
  if (percent < 0.05) return null // Don't show labels for tiny slices
  
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}

/**
 * Pie chart displaying session distribution by mode.
 * 
 * @example
 * ```tsx
 * <SessionsByModeChart 
 *   data={[
 *     { mode: 'Interactive', count: 200, percentage: 44.4 },
 *     { mode: 'Background', count: 150, percentage: 33.3 },
 *   ]} 
 * />
 * ```
 */
export function SessionsByModeChart({
  data,
  height = chartDefaults.height,
}: SessionsByModeChartProps) {
  // Empty state
  if (!data || data.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        <PieChartIcon className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No data available</p>
      </div>
    )
  }

  return (
    <div 
      role="img" 
      aria-label="Sessions by mode pie chart showing distribution of session types"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <PieChart>
          <Pie
            data={data as unknown as Array<Record<string, unknown>>}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel as unknown as undefined}
            outerRadius={80}
            dataKey="count"
            nameKey="mode"
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={modeColors[entry.mode] ?? 'hsl(var(--muted))'}
              />
            ))}
          </Pie>
          
          <Tooltip 
            contentStyle={tooltipStyles.contentStyle}
            labelStyle={tooltipStyles.labelStyle}
            formatter={(value, name) => [
              `${value} sessions`,
              String(name)
            ]}
          />
          
          <Legend 
            verticalAlign="bottom"
            height={36}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
